import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useOrganization } from "../OrganizationContext"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"
import PaidMediaAccountSetup from "../components/PaidMediaAccountSetup"
import {
  connectMetaPixel,
  completeMetaOAuth,
  disconnectMetaPixel,
  listMetaPixelConnections,
  loadLatestMetaPixelAudit,
  runMetaPixelAudit,
  startMetaOAuth,
} from "../api/metaPixels"
import { navigateCurrentWindow } from "../utils/browserNavigation"
import "./PaidMedia.css"

const DATE_RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
]

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Number(value) || 0,
  )

const formatDate = (value, fallback = "No recent activity") => {
  if (!value) return fallback
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return fallback
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed)
}

const parameterLabel = (parameter) =>
  parameter.kind === "match_key" ? "Customer information" : "Event parameter"

const auditStatusLabel = (status) => ({
  pass: "Passed",
  warning: "Warnings",
  failure: "Needs attention",
  incomplete: "Incomplete",
  unknown: "Unknown",
  info: "Informational",
  not_observed: "Not observed",
  active: "Configured with trigger",
  dormant: "Configured, dormant",
  missing: "Tag missing",
  wrong_pixel: "Wrong Pixel",
}[status] || "Unknown")

export default function PaidMedia() {
  const {
    organizations,
    selectedOrgId,
    isAutomotive = true,
    appName = isAutomotive ? "ASC TuneUp" : "TuneUp",
  } = useOrganization()
  const selectedOrg = useMemo(
    () => organizations.find((organization) => organization.id === selectedOrgId),
    [organizations, selectedOrgId],
  )
  const [connections, setConnections] = useState([])
  const [selectedConnectionId, setSelectedConnectionId] = useState("")
  const [connectionsLoading, setConnectionsLoading] = useState(true)
  const [showConnect, setShowConnect] = useState(false)
  const [oauthSessionId, setOauthSessionId] = useState("")
  const [metaUser, setMetaUser] = useState(null)
  const [discoveredPixels, setDiscoveredPixels] = useState([])
  const [authorizing, setAuthorizing] = useState(false)
  const [completingOAuth, setCompletingOAuth] = useState(false)
  const [connectingPixelId, setConnectingPixelId] = useState("")
  const [days, setDays] = useState(30)
  const [audit, setAudit] = useState(null)
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditRunning, setAuditRunning] = useState(false)
  const [expandedEvents, setExpandedEvents] = useState(() => new Set())
  const [status, setStatus] = useState(null)
  const [gtmAudit, setGtmAudit] = useState(null)
  const oauthCallbackHandledRef = useRef(false)

  const selectedConnection = useMemo(
    () => connections.find((connection) => connection.id === selectedConnectionId),
    [connections, selectedConnectionId],
  )

  const loadConnections = useCallback(async () => {
    if (!selectedOrgId) {
      setConnections([])
      setSelectedConnectionId("")
      setConnectionsLoading(false)
      return
    }
    setConnectionsLoading(true)
    try {
      const result = await listMetaPixelConnections(selectedOrgId)
      const nextConnections = result.connections || []
      setConnections(nextConnections)
      setSelectedConnectionId((current) =>
        nextConnections.some((connection) => connection.id === current)
          ? current
          : nextConnections[0]?.id || "",
      )
    } catch (error) {
      setStatus({ type: "error", message: error.message })
      setConnections([])
      setSelectedConnectionId("")
    } finally {
      setConnectionsLoading(false)
    }
  }, [selectedOrgId])

  useEffect(() => {
    setStatus(null)
    setDiscoveredPixels([])
    setOauthSessionId("")
    setMetaUser(null)
    setAudit(null)
    setGtmAudit(null)
    loadConnections()
  }, [loadConnections])

  const applyOAuthResult = useCallback((result) => {
    const pixels = result?.pixels || []
    const inaccessibleAccounts = result?.inaccessible_ad_accounts || []
    setOauthSessionId(result?.oauth_session_id || "")
    setMetaUser(result?.meta_user || null)
    setDiscoveredPixels(pixels)
    setShowConnect(true)
    setAuthorizing(false)
    if (inaccessibleAccounts.length) {
      const accountNames = inaccessibleAccounts
        .slice(0, 3)
        .map((account) => account.name || account.id)
        .filter(Boolean)
        .join(", ")
      const remainingCount = Math.max(0, inaccessibleAccounts.length - 3)
      const accountSummary = accountNames
        ? ` (${accountNames}${remainingCount ? ` and ${remainingCount} more` : ""})`
        : ""
      const accessibleSummary = pixels.length
        ? ` You can still add ${pixels.length === 1 ? "the accessible Pixel" : `${pixels.length} accessible Pixels`} shown below.`
        : ""
      setStatus({
        type: pixels.length ? "info" : "error",
        message:
          `Meta connected, but ${inaccessibleAccounts.length === 1 ? "a business ad account" : `${inaccessibleAccounts.length} business ad accounts`} could not be inspected${accountSummary}. Reconnect and select those accounts if Meta asks which assets to share.` +
          accessibleSummary,
      })
    } else if (!pixels.length) {
      setStatus({
        type: "info",
        message: "Meta sign-in succeeded, but no Pixels were found in the ad accounts assigned to your Meta user.",
      })
    } else {
      setStatus({
        type: "success",
        message: `Meta connected. Choose ${pixels.length === 1 ? "the Pixel" : "one or more Pixels"} to add.`,
      })
    }
  }, [])

  useEffect(() => {
    const handleOAuthMessage = (event) => {
      if (event.origin !== window.location.origin) return
      if (event?.data?.type !== "meta-oauth-complete") return
      if (event.data.error) {
        setStatus({ type: "error", message: event.data.error })
        setAuthorizing(false)
      } else {
        applyOAuthResult(event.data.result)
      }
    }
    window.addEventListener("message", handleOAuthMessage)
    return () => window.removeEventListener("message", handleOAuthMessage)
  }, [applyOAuthResult])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get("code")
    const state = params.get("state")
    const oauthError = params.get("error_description") || params.get("error")
    const isGtmCallback =
      params.get("oauth_provider") === "gtm" ||
      sessionStorage.getItem("gtm-oauth-pending") === "true"
    if (
      isGtmCallback ||
      (!code && !oauthError) ||
      oauthCallbackHandledRef.current ||
      !selectedOrgId
    ) return
    oauthCallbackHandledRef.current = true
    setCompletingOAuth(true)
    setShowConnect(true)

    const reportResult = (payload) => {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: "meta-oauth-complete", ...payload },
          window.location.origin,
        )
        window.close()
        return true
      }
      window.history.replaceState({}, document.title, window.location.pathname)
      return false
    }

    if (oauthError) {
      if (!reportResult({ error: oauthError })) {
        setStatus({ type: "error", message: oauthError })
        setCompletingOAuth(false)
      }
      return
    }

    completeMetaOAuth(selectedOrgId, code, state)
      .then((result) => {
        if (!reportResult({ result })) applyOAuthResult(result)
      })
      .catch((error) => {
        if (!reportResult({ error: error.message })) {
          setStatus({ type: "error", message: error.message })
        }
      })
      .finally(() => setCompletingOAuth(false))
  }, [applyOAuthResult, selectedOrgId])

  useEffect(() => {
    let cancelled = false
    setAudit(null)
    setExpandedEvents(new Set())
    if (!selectedOrgId || !selectedConnectionId) return () => { cancelled = true }
    setAuditLoading(true)
    loadLatestMetaPixelAudit(selectedOrgId, selectedConnectionId)
      .then((result) => {
        if (!cancelled) setAudit(result.audit || null)
      })
      .catch((error) => {
        if (!cancelled) setStatus({ type: "error", message: error.message })
      })
      .finally(() => {
        if (!cancelled) setAuditLoading(false)
      })
    return () => { cancelled = true }
  }, [selectedConnectionId, selectedOrgId])

  const handleRunAudit = async () => {
    if (!selectedOrgId || !selectedConnectionId || auditRunning) return
    setAuditRunning(true)
    setStatus(null)
    try {
      const result = await runMetaPixelAudit(
        selectedOrgId,
        selectedConnectionId,
        days,
      )
      setAudit(result.audit || null)
      setExpandedEvents(new Set())
      setConnections((current) =>
        current.map((connection) =>
          connection.id === selectedConnectionId
            ? { ...connection, last_synced_at: result.audit?.audited_at }
            : connection,
        ),
      )
      setStatus({
        type: "success",
        message: isAutomotive
          ? "Meta automotive event audit completed."
          : "Meta event audit completed.",
      })
    } catch (error) {
      setStatus({ type: "error", message: error.message })
    } finally {
      setAuditRunning(false)
    }
  }

  const handleStartOAuth = async () => {
    if (!selectedOrgId || authorizing) return
    setAuthorizing(true)
    setStatus(null)
    setDiscoveredPixels([])
    setOauthSessionId("")
    setMetaUser(null)
    try {
      const result = await startMetaOAuth(selectedOrgId)
      if (!result.authorization_url) throw new Error("Meta OAuth did not return a sign-in URL")
      navigateCurrentWindow(result.authorization_url)
    } catch (error) {
      setStatus({ type: "error", message: error.message })
      setAuthorizing(false)
    }
  }

  const handleConnect = async (pixel) => {
    setConnectingPixelId(pixel.id)
    setStatus(null)
    try {
      const result = await connectMetaPixel(selectedOrgId, oauthSessionId, pixel)
      await loadConnections()
      setSelectedConnectionId(result.connection?.id || "")
      setDiscoveredPixels((current) => current.filter((item) => item.id !== pixel.id))
      setStatus({ type: "success", message: `${pixel.name} is connected.` })
    } catch (error) {
      setStatus({ type: "error", message: error.message })
    } finally {
      setConnectingPixelId("")
    }
  }

  const handleDisconnect = async () => {
    if (!selectedConnection) return
    if (!window.confirm(`Disconnect ${selectedConnection.pixel_name}?`)) return
    setStatus(null)
    try {
      await disconnectMetaPixel(selectedOrgId, selectedConnection.id)
      setAudit(null)
      await loadConnections()
      setStatus({ type: "success", message: "Meta Pixel disconnected." })
    } catch (error) {
      setStatus({ type: "error", message: error.message })
    }
  }

  const toggleEvent = (eventName) => {
    setExpandedEvents((current) => {
      const next = new Set(current)
      if (next.has(eventName)) next.delete(eventName)
      else next.add(eventName)
      return next
    })
  }

  const events = audit?.events || []
  const summary = audit?.summary || {}
  const auditChecklist = audit?.documented_events || audit?.required_events || []
  const auditProfileName = audit?.audit_profile?.name ||
    (isAutomotive ? "ASC automotive" : "General")
  const pageViewBaseline = audit?.baseline_events?.find(
    (event) => event.name === "PageView",
  )
  const gtmEventsByName = useMemo(
    () => new Map([
      ...(gtmAudit?.event_rows || []).map((event) => [event.name || event.event_name, event]),
      ...(gtmAudit?.route_rows || [])
        .filter((route) => route.platform === "meta")
        .map((route) => [route.destination_event_name, {
          ...route,
          name: route.destination_event_name,
          configured: route.configured,
          parameters: [],
        }]),
    ]),
    [gtmAudit],
  )

  return (
    <div className="page-container page-container--wide paid-media-meta">
      <header className="ui-page-header paid-media-meta__header">
        <div>
          <Badge variant="accent">Paid Media</Badge>
          <h2 className="ui-page-title">Account paid media audit</h2>
          <p className="ui-subtitle">
            {isAutomotive
              ? "Audit every event and aggregate parameter Meta receives against ASC automotive guidance."
              : "Audit the events and aggregate parameters Meta receives against this organization's rules."}
          </p>
          {selectedOrg && (
            <p className="paid-media-meta__org">Organization: {selectedOrg.name}</p>
          )}
        </div>
        <Button variant="outline" onClick={() => setShowConnect((current) => !current)}>
          {showConnect ? "Close setup" : "Connect Meta Pixel"}
        </Button>
      </header>

      {status && (
        <div
          className={`paid-media-meta__status paid-media-meta__status--${status.type}`}
          role={status.type === "error" ? "alert" : "status"}
        >
          {status.message}
        </div>
      )}

      {isAutomotive && (
        <PaidMediaAccountSetup
          organizationId={selectedOrgId}
          connections={connections}
          selectedConnectionId={selectedConnectionId}
          onSelectConnection={setSelectedConnectionId}
          onGtmAuditChange={setGtmAudit}
        />
      )}

      {showConnect && (
        <Card className="paid-media-meta__connect-card">
          <CardHeader>
            <CardTitle>Connect with Meta</CardTitle>
            <CardDescription>
              Sign in to Meta and approve read access. {appName} will list Pixels from the
              ad accounts available to your Meta user, then you choose which Pixel to connect.
              Credentials remain encrypted on the server.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!oauthSessionId && (
              <div className="paid-media-meta__oauth-actions">
                <Button onClick={handleStartOAuth} disabled={authorizing || completingOAuth}>
                  {completingOAuth
                    ? "Completing Meta sign-in…"
                    : authorizing
                      ? "Waiting for Meta…"
                      : "Continue with Meta"}
                </Button>
                <p>
                  Meta will ask for <strong>ads_read</strong> so {appName} can list the ad
                  accounts assigned to you, discover their Pixels, and read aggregate Pixel
                  diagnostics. It does not request portfolio-wide management access.
                </p>
              </div>
            )}

            {oauthSessionId && metaUser?.name && (
              <div className="paid-media-meta__identity">
                <Badge variant="subtle">Signed in</Badge>
                <span>{metaUser.name}</span>
                <Button variant="ghost" size="sm" onClick={handleStartOAuth}>
                  Use another Meta account
                </Button>
              </div>
            )}

            {oauthSessionId && discoveredPixels.length === 0 && (
              <p className="paid-media-meta__table-message">
                No additional Pixels are available from this Meta sign-in.
              </p>
            )}

            {discoveredPixels.length > 0 && (
              <TableContainer className="paid-media-meta__discovery-table">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Pixel</TableHeaderCell>
                      <TableHeaderCell>Owning ad account</TableHeaderCell>
                      <TableHeaderCell>Last activity</TableHeaderCell>
                      <TableHeaderCell align="right">Action</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discoveredPixels.map((pixel) => (
                      <TableRow key={pixel.id}>
                        <TableCell>
                          <strong>{pixel.name}</strong>
                          <span className="paid-media-meta__secondary">ID {pixel.id}</span>
                        </TableCell>
                        <TableCell>{pixel.ad_account_name || "Business Portfolio asset"}</TableCell>
                        <TableCell>{formatDate(pixel.last_fired_time)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="sm"
                            onClick={() => handleConnect(pixel)}
                            disabled={Boolean(connectingPixelId)}
                          >
                            {connectingPixelId === pixel.id ? "Connecting…" : "Connect"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {connectionsLoading ? (
        <Card><CardContent>Loading Meta Pixel connections…</CardContent></Card>
      ) : connections.length === 0 ? (
        <Card className="paid-media-meta__empty">
          <CardHeader>
            <CardTitle>No Meta Pixel connected</CardTitle>
            <CardDescription>
              {isAutomotive
                ? "Connect a Pixel to audit its automotive events and parameter coverage."
                : "Connect a Pixel to audit its events and parameter coverage."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setShowConnect(true)}>Connect your first Pixel</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="paid-media-meta__toolbar">
              <label className="paid-media-meta__select-field">
                <span>Meta Pixel</span>
                <select
                  value={selectedConnectionId}
                  onChange={(event) => setSelectedConnectionId(event.target.value)}
                >
                  {connections.map((connection) => (
                    <option key={connection.id} value={connection.id}>
                      {connection.pixel_name} ({connection.pixel_id})
                    </option>
                  ))}
                </select>
              </label>
              <label className="paid-media-meta__select-field">
                <span>Date range</span>
                <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
                  {DATE_RANGES.map((range) => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </label>
              <div className="paid-media-meta__toolbar-actions">
                <Button onClick={handleRunAudit} disabled={auditRunning || auditLoading}>
                  {auditRunning ? "Running audit…" : "Run Meta audit"}
                </Button>
                <Button variant="ghost" onClick={handleDisconnect}>Disconnect</Button>
              </div>
            </CardContent>
          </Card>

          {auditLoading ? (
            <Card><CardContent>Loading the latest saved audit…</CardContent></Card>
          ) : !audit ? (
            <Card className="paid-media-meta__empty">
              <CardHeader>
                <CardTitle>No saved audit</CardTitle>
                <CardDescription>
                  Run the first audit to inspect all events Meta observed for this Pixel.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleRunAudit} disabled={auditRunning}>
                  {auditRunning ? "Running audit…" : "Run first Meta audit"}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="paid-media-meta__summary-grid">
                <AuditMetric label="Overall status" value={auditStatusLabel(summary.status)} status={summary.status} />
                {summary.required_events_total > 0 && (
                  <AuditMetric label="Required events" value={`${formatNumber(summary.required_events_observed)} / ${formatNumber(summary.required_events_total)}`} />
                )}
                <AuditMetric label="Event types" value={formatNumber(summary.event_types)} />
                <AuditMetric label="Events received" value={formatNumber(summary.events_received)} />
                <AuditMetric label="Findings" value={formatNumber((summary.failure_count || 0) + (summary.warning_count || 0))} />
                <AuditMetric label="Last audit" value={formatDate(audit.audited_at, "Not audited")} compact />
              </div>

              {auditChecklist.length > 0 && <Card>
                <CardHeader split>
                  <div>
                    <CardTitle>{auditProfileName} event checklist</CardTitle>
                    <CardDescription>
                      {auditChecklist.length} configured rule events, with
                      {isAutomotive ? " GTM configuration kept separate from" : ""} Meta
                      activity for the {audit.days}-day audit period.
                    </CardDescription>
                  </div>
                  <AuditStatusBadge status={summary.status} />
                </CardHeader>
                <CardContent>
                  {pageViewBaseline && (
                    <div className="paid-media-meta__baseline">
                      <div>
                        <strong>PageView baseline</strong>
                        <span>
                          Pixel installation signal · {pageViewBaseline.observed
                            ? `${formatNumber(pageViewBaseline.count)} received`
                            : "Not observed"}
                        </span>
                      </div>
                      <div className="paid-media-meta__baseline-statuses">
                        {isAutomotive && (
                          <>
                            <span>GTM</span>
                            <AuditStatusBadge
                              status={gtmEventsByName.get("PageView")?.status || "unknown"}
                            />
                          </>
                        )}
                        <span>Meta</span>
                        <AuditStatusBadge status={pageViewBaseline.status} />
                      </div>
                    </div>
                  )}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>Event</TableHeaderCell>
                          <TableHeaderCell>Rule</TableHeaderCell>
                          {isAutomotive && <TableHeaderCell>GTM configuration</TableHeaderCell>}
                          <TableHeaderCell>Meta activity</TableHeaderCell>
                          <TableHeaderCell align="right">Received</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {auditChecklist.map((event) => {
                          const gtmEvent = gtmEventsByName.get(event.name)
                          return (
                            <TableRow key={event.name}>
                              <TableCell>
                                <strong>{event.name}</strong>
                              </TableCell>
                              <TableCell>
                                {event.requirement === "required"
                                  ? "Required"
                                  : "Recommended when used"}
                              </TableCell>
                              {isAutomotive && (
                                <TableCell>
                                  <AuditStatusBadge status={gtmEvent?.status || "unknown"} />
                                  {gtmEvent?.configured && (
                                    <span className="paid-media-meta__secondary">
                                      {gtmEvent.parameters?.length || 0} configured
                                      {gtmEvent.missing_required_parameters?.length
                                        ? ` · ${gtmEvent.missing_required_parameters.length} required missing`
                                        : ""}
                                      {gtmEvent.missing_recommended_parameters?.length
                                        ? ` · ${gtmEvent.missing_recommended_parameters.length} recommended missing`
                                        : ""}
                                    </span>
                                  )}
                                </TableCell>
                              )}
                              <TableCell><AuditStatusBadge status={event.status} /></TableCell>
                              <TableCell align="right">{formatNumber(event.count)}</TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>}

              <Card>
                <CardHeader split>
                  <div>
                    <CardTitle>All reported events</CardTitle>
                    <CardDescription>
                      Open an event to review aggregate parameter coverage, matching keys, and findings.
                    </CardDescription>
                  </div>
                  <Badge variant="outline">{audit.is_complete ? "Complete data" : "Incomplete data"}</Badge>
                </CardHeader>
                <CardContent>
                  {events.length === 0 ? (
                    <p className="paid-media-meta__table-message">
                      Meta did not report any events for this audit period.
                    </p>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>Event</TableHeaderCell>
                            <TableHeaderCell>Status</TableHeaderCell>
                            <TableHeaderCell align="right">Received</TableHeaderCell>
                            <TableHeaderCell>Last activity</TableHeaderCell>
                            <TableHeaderCell align="right">Findings</TableHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {events.map((event) => (
                            <EventRows
                              key={event.name}
                              event={event}
                              expanded={expandedEvents.has(event.name)}
                              onToggle={() => toggleEvent(event.name)}
                              isAutomotive={isAutomotive}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  <div className="paid-media-meta__privacy-note">
                    {(audit.limitations || []).map((limitation) => (
                      <p key={limitation}>{limitation}</p>
                    ))}
                    <p>No raw parameter values, URLs, or customer data are retrieved or stored.</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  )
}

function EventRows({ event, expanded, onToggle, isAutomotive }) {
  const parameters = event.parameters || []
  const matchKeys = event.match_keys || []
  const findings = event.findings || []
  return (
    <>
      <TableRow>
        <TableCell>
          <button
            type="button"
            className="paid-media-meta__event-toggle"
            onClick={onToggle}
            aria-expanded={expanded}
          >
            <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
            <strong>{event.name}</strong>
          </button>
          <span className="paid-media-meta__secondary">
            {event.classification === "automotive_required"
              ? isAutomotive ? "Required automotive event" : "Required event"
              : event.classification === "automotive_documented"
                ? isAutomotive ? "Documented automotive event" : "Documented event"
              : event.classification === "profile_required"
                ? "Required by organization rules"
              : event.classification === "profile_documented"
                ? "Included in organization rules"
              : event.classification === "standard"
                ? "Meta standard event"
                : "Custom event"}
          </span>
        </TableCell>
        <TableCell><AuditStatusBadge status={event.status} /></TableCell>
        <TableCell align="right">{formatNumber(event.count)}</TableCell>
        <TableCell>{formatDate(event.last_received_at)}</TableCell>
        <TableCell align="right">{formatNumber(findings.length)}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="paid-media-meta__parameter-row">
          <TableCell colSpan={5}>
            {findings.length > 0 && (
              <div className="paid-media-meta__findings">
                {findings.map((finding) => (
                  <div
                    className={`paid-media-meta__finding paid-media-meta__finding--${finding.severity}`}
                    key={`${finding.code}-${finding.parameter_name || "event"}`}
                  >
                    <AuditStatusBadge status={finding.severity} />
                    <span>{finding.message}</span>
                  </div>
                ))}
              </div>
            )}
            {parameters.length === 0 && matchKeys.length === 0 ? (
              <p className="paid-media-meta__parameter-empty">
                Meta did not return parameter coverage for this event.
              </p>
            ) : (
              <>
                <DimensionGroup title="Event parameters" dimensions={parameters} />
                <DimensionGroup title="Customer matching keys" dimensions={matchKeys} />
              </>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function DimensionGroup({ title, dimensions }) {
  if (!dimensions.length) return null
  return (
    <section className="paid-media-meta__dimension-group">
      <h4>{title}</h4>
      <div className="paid-media-meta__parameters">
        {dimensions.map((parameter) => (
          <div
            className={`paid-media-meta__parameter paid-media-meta__parameter--${parameter.status}`}
            key={`${parameter.kind}-${parameter.name}`}
          >
            <div>
              <strong>{parameter.name}</strong>
              <span>
                {parameterLabel(parameter)} · {parameter.requirement || "informational"}
              </span>
            </div>
            <div className="paid-media-meta__parameter-coverage">
              <AuditStatusBadge status={parameter.status} />
              <span>
                {parameter.coverage_percent == null
                  ? "Coverage unavailable"
                  : `${formatNumber(parameter.count)} events · ${parameter.coverage_percent}%`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function AuditStatusBadge({ status }) {
  const normalizedStatus = status === "failure" ? "failure" : status || "unknown"
  return (
    <Badge
      variant="subtle"
      className={`paid-media-meta__audit-badge paid-media-meta__audit-badge--${normalizedStatus}`}
    >
      {auditStatusLabel(normalizedStatus)}
    </Badge>
  )
}

function AuditMetric({ label, value, status, compact = false }) {
  return (
    <Card>
      <CardContent>
        <span className="paid-media-meta__metric-label">{label}</span>
        <strong className={`paid-media-meta__metric${compact ? " paid-media-meta__metric--date" : ""}`}>
          {value}
        </strong>
        {status && <AuditStatusBadge status={status} />}
      </CardContent>
    </Card>
  )
}
